/**
 * Property Config tab — Yardi Config only.
 * Access Fee Config lives on the main PropertyEdit form (same as Angular property-edit).
 * Yardi UI matches Angular: segmented Yes/No, date + calendar on one row with invite.
 */
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { Loader2, Server } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import apiClient from "@/lib/api/apiClient";
import { showSuccess, showApiError, showWarning } from "@/lib/utils/notifications";
import { DatePicker } from "@/components/ui/DatePicker";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SectionCard } from "@/components/ui/SectionCard";
import { companyEditConfirmPresentation } from "@/features/company/constants";
import companyFormStyles from "@/features/company/styles/companyAdd.module.css";
import tabStyles from "@/features/company/styles/companyInfoTab.module.css";
import { COMM_FIELD_BASE, COMM_LABEL } from "@/lib/styles/companyEditTabShell";
import { cn } from "@/lib/utils/helpers";
import type {
    PropertyConfigTabHandle,
    PropertyConfigTabProps,
    PropertyYardiBaseline,
} from "../types";
import { YardiResidentSyncPanel } from "./YardiResidentSyncPanel";

const FIELD_CLASS = cn(COMM_FIELD_BASE, "h-10 w-full max-w-full sm:max-w-[280px]");

/** `YYYY-MM-DD` for date state (avoids UTC shift from toISOString). */
function formatLocalDate(d: Date): string {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${day}`;
}

/**
 * Normalize API `automation_from_date` (date-only, ISO, or timestamp) for date input.
 * Angular uses moment(..., 'YYYY-MM-DD') then formats; we never use UTC midnight slice.
 */
function automationFromDateToInputValue(raw: unknown): string {
    if (raw == null || raw === "") return "";
    if (typeof raw === "number" && Number.isFinite(raw)) {
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? "" : formatLocalDate(d);
    }
    const s = String(raw).trim();
    if (!s) return "";
    const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`;
    const d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
    return Number.isNaN(d.getTime()) ? "" : formatLocalDate(d);
}

function baselineFromPc(pc: Record<string, unknown> | undefined): PropertyYardiBaseline {
    if (!pc) {
        return {
            yardiCode: "",
            acceptAutomationInvite: "0",
            automationFromDate: "",
            agentId: "",
        };
    }
    const acceptAutomationInvite =
        pc.accept_automation_invite != null ? String(pc.accept_automation_invite) : "0";
    let automationFromDate = automationFromDateToInputValue(pc.automation_from_date);
    /** Angular form defaults effective date to today; if API has Yes but no date, match that UX. */
    if (acceptAutomationInvite === "1" && !automationFromDate) {
        automationFromDate = formatLocalDate(new Date());
    }
    return {
        yardiCode: String(pc.yardi_code ?? ""),
        acceptAutomationInvite,
        automationFromDate,
        agentId: String(pc.fk_agent_id ?? pc.agent_id ?? ""),
    };
}

export const PropertyConfigTab = forwardRef<PropertyConfigTabHandle, PropertyConfigTabProps>(
    function PropertyConfigTab(
        {
            property,
            propertyId,
            onRefresh,
        }: PropertyConfigTabProps,
        ref
    ) {
    const { hasPolicy } = useUserRole();

    const [yardiCode, setYardiCode] = useState("");
    const [acceptAutomationInvite, setAcceptAutomationInvite] = useState("0");
    const [automationFromDate, setAutomationFromDate] = useState("");
    const [agentId, setAgentId] = useState("");
    const [staffList, setStaffList] = useState<Record<string, unknown>[]>([]);
    const [saving, setSaving] = useState(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const [baseline, setBaseline] = useState<PropertyYardiBaseline>(() => ({
        yardiCode: "",
        acceptAutomationInvite: "0",
        automationFromDate: "",
        agentId: "",
    }));
    const hydrateFromPc = useCallback((pc: Record<string, unknown> | undefined) => {
        const b = baselineFromPc(pc);
        setYardiCode(b.yardiCode);
        setAcceptAutomationInvite(b.acceptAutomationInvite);
        setAutomationFromDate(b.automationFromDate);
        setAgentId(b.agentId);
        setBaseline(b);
    }, []);

    useEffect(() => {
        if (property == null) return;
        const pc = (property.propertyConfig ?? property) as Record<string, unknown>;
        hydrateFromPc(pc);
    }, [property, hydrateFromPc]);

    useEffect(() => {
        apiClient
            .get(`/api/property/${propertyId}`)
            .then((res) => {
                const data = res.data as Record<string, unknown>;
                const pc = (data?.propertyConfig ?? data) as Record<string, unknown>;
                hydrateFromPc(pc);
            })
            .catch(() => {});
    }, [propertyId, hydrateFromPc]);

    useEffect(() => {
        apiClient
            .get("/api/property_staff", { params: { property_id: propertyId } })
            .then((res) => setStaffList(Array.isArray(res.data) ? res.data : []))
            .catch(() => setStaffList([]));
    }, [propertyId]);

    const isYardiDirty =
        yardiCode !== baseline.yardiCode ||
        acceptAutomationInvite !== baseline.acceptAutomationInvite ||
        automationFromDate !== baseline.automationFromDate ||
        agentId !== baseline.agentId;

    const performSave = useCallback(async (): Promise<boolean> => {
        setSaving(true);
        try {
            await apiClient.post(`/api/property/config/${propertyId}`, {
                yardi_code: yardiCode.trim(),
                agent_id: agentId,
                accept_automation_invite: acceptAutomationInvite,
                automation_from_date: automationFromDate || null,
            });
            showSuccess("Property Updated  Successfully!");
            const trimmedCode = yardiCode.trim();
            setYardiCode(trimmedCode);
            setBaseline({
                yardiCode: trimmedCode,
                acceptAutomationInvite,
                automationFromDate,
                agentId,
            });
            onRefresh?.();
            return true;
        } catch (error) {
            showApiError(error);
            return false;
        } finally {
            setSaving(false);
        }
    }, [
        agentId,
        acceptAutomationInvite,
        automationFromDate,
        onRefresh,
        propertyId,
        yardiCode,
    ]);

    useImperativeHandle(
        ref,
        () => ({
            getYardiDirty: () =>
                yardiCode !== baseline.yardiCode ||
                acceptAutomationInvite !== baseline.acceptAutomationInvite ||
                automationFromDate !== baseline.automationFromDate ||
                agentId !== baseline.agentId,
        }),
        [
            acceptAutomationInvite,
            agentId,
            automationFromDate,
            baseline.acceptAutomationInvite,
            baseline.agentId,
            baseline.automationFromDate,
            baseline.yardiCode,
            yardiCode,
        ]
    );

    // Guard after all hooks so hook order stays stable across renders (Rules of Hooks).
    if (!hasPolicy(["yardi_config"])) return null;

    /** Angular `submitPropertyConfig`: validate → same Save Changes? dialog as property header (via parent). */
    const handleSaveYardiConfig = () => {
        if (!yardiCode.trim()) {
            showWarning("Yardi Code is required.");
            return;
        }
        if (acceptAutomationInvite === "1" && !automationFromDate) {
            return;
        }
        if (!isYardiDirty) {
            showWarning("No changes detected. Modify a field before saving.");
            return;
        }
        setSaveConfirmOpen(true);
    };

    const staffName = (s: Record<string, unknown>) =>
        (s.name as string) || `${s.first_name || ""} ${s.last_name || ""}`.trim() || "Unknown";

    /** Same Yes/No layout as PropertyIntegrationsTab Utility Concierge (gap-2, separate rounded buttons). */
    const segmentBase =
        "min-w-[3.5rem] sm:min-w-[4.5rem] px-3 sm:px-4 h-[36px] sm:h-[38px] rounded-md border shadow-sm text-xs sm:text-sm font-medium transition-colors focus-visible:outline focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)] focus-visible:ring-offset-1";
    const segmentOn = "bg-[rgb(21_151_255)] text-white border-[rgb(21_151_255)]";
    const segmentOff = "bg-white text-slate-800 hover:bg-slate-50 border-slate-300";

    return (
        <div
            className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
            data-testid="property-config-tab"
            data-independent-save-scope="true"
        >
            <SectionCard
                icon={Server}
                title="Yardi Config"
                testId="property-config-yardi-section"
                titleTestId="property-config-yardi-section-title"
            >
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
            <form
                className="space-y-5"
                data-testid="property-config-form"
                onSubmit={(e) => {
                    e.preventDefault();
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                }}
            >
                <div>
                    <label className={COMM_LABEL} htmlFor="yardi_code">
                        Yardi Code<span className="font-semibold text-[#dc2626]">*</span>
                    </label>
                    <input
                        id="yardi_code"
                        name="yardi_code"
                        type="text"
                        placeholder="Yardi Code"
                        value={yardiCode}
                        onChange={(e) => setYardiCode(e.target.value)}
                        data-testid="property-config-yardi-code-input"
                        className={FIELD_CLASS}
                        required
                    />
                    {!yardiCode.trim() && (
                        <p className="mt-1 text-sm text-red-600">Yardi Code is required.</p>
                    )}
                </div>

                <div className="max-w-3xl space-y-4">
                    <div>
                        <label className={COMM_LABEL}>
                            Accept Automation Invite<span className="font-semibold text-[#dc2626]">*</span>
                        </label>
                        <div
                            className="inline-flex shrink-0 items-center gap-2"
                            role="group"
                            aria-label="Accept Automation Invite"
                        >
                            <button
                                type="button"
                                id="accept_automation_invite_yes"
                                aria-pressed={acceptAutomationInvite === "1"}
                                data-testid="property-config-automation-yes-button"
                                className={`${segmentBase} ${acceptAutomationInvite === "1" ? segmentOn : segmentOff}`}
                                onClick={() => {
                                    setAcceptAutomationInvite("1");
                                    /** Match Angular: form defaults effective date to today when enabling Yes. */
                                    setAutomationFromDate((prev) =>
                                        prev.trim() ? prev : formatLocalDate(new Date())
                                    );
                                }}
                            >
                                Yes
                            </button>
                            <button
                                type="button"
                                id="accept_automation_invite_no"
                                aria-pressed={acceptAutomationInvite === "0"}
                                data-testid="property-config-automation-no-button"
                                className={`${segmentBase} ${acceptAutomationInvite === "0" ? segmentOn : segmentOff}`}
                                onClick={() => setAcceptAutomationInvite("0")}
                            >
                                No
                            </button>
                        </div>
                    </div>

                    {acceptAutomationInvite === "1" && (
                        <div className="max-w-full sm:max-w-[280px]">
                            <label className={COMM_LABEL} htmlFor="automation_from_date">
                                Change effective date
                            </label>
                            <DatePicker
                                id="automation_from_date"
                                name="automation_from_date"
                                value={automationFromDate}
                                onChange={(v) => setAutomationFromDate(v)}
                                dataTestId="property-config-automation-date"
                                allowFutureDates={true}
                                showHelperText={false}
                                placeholder="MM/DD/YYYY"
                                className="text-xs sm:text-sm"
                            />
                            {!automationFromDate && (
                                <p className="mt-1 text-sm text-red-600" data-testid="property-config-automation-date-error">
                                    Due Date is required.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div>
                    <label className={COMM_LABEL} htmlFor="agent_id">
                        Select Agent
                    </label>
                    <select
                        id="agent_id"
                        name="agent_id"
                        value={agentId}
                        onChange={(e) => setAgentId(e.target.value)}
                        data-testid="property-config-agent-select"
                        className={FIELD_CLASS}
                    >
                        <option value="">Choose Agent...</option>
                        {staffList.map((s) => (
                            <option key={String(s.user_id)} value={String(s.user_id)}>
                                {staffName(s)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="pt-1">
                    <button
                        type="button"
                        className={cn(companyFormStyles.btnPrimary, "min-w-[100px] gap-2")}
                        disabled={saving || !yardiCode.trim() || !isYardiDirty}
                        data-testid="property-config-save-button"
                        onClick={handleSaveYardiConfig}
                    >
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                Saving...
                            </>
                        ) : (
                            "Save"
                        )}
                    </button>
                </div>
            </form>
            </div>
            <div data-testid="property-config-yardi-resident-sync-section">
                <YardiResidentSyncPanel
                    propertyId={propertyId}
                    companyId={(property?.fk_company_id ?? property?.company_id ?? "") as string | number}
                    propertyCode={baseline.yardiCode.trim()}
                    propertyName={String(property?.property_name ?? property?.name ?? "")}
                    onConfirmed={onRefresh}
                />
            </div>
            </div>
            </SectionCard>
            <ConfirmDialog
                isOpen={saveConfirmOpen}
                title="Save Changes?"
                message="Are you sure you want to save these Yardi configuration changes?"
                primaryLabel="Save"
                secondaryLabel="Cancel"
                onPrimary={() => {
                    setSaveConfirmOpen(false);
                    void performSave();
                }}
                onSecondary={() => setSaveConfirmOpen(false)}
                {...companyEditConfirmPresentation}
            />
        </div>
    );
    }
);
